import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import AppLoader from 'Src/components/AppLoader';
import { useCheck } from 'Src/feature/check/useCheck';
import { FlipAndTipCheck } from 'Src/feature/flip-and-tip/FlipAndTipCheck';
import { FlipAndTipForm } from 'Src/feature/flip-and-tip/FlipAndTipForm';
import { Page } from 'Src/feature/page/Page';
import { PageMain } from 'Src/feature/page/PageMain';
import { usePayment } from 'Src/feature/payment/usePayment';
import { usePaymentFlow } from 'Src/feature/payment/usePaymentFlow';
import { useToast } from 'Src/feature/toasts/useToast';
import useRoute from 'Src/routes/useRoute';
import { log } from 'Src/utils/log';

import styles from './FlipAndTipStartPage.module.scss';

const sectionId = 'FlipAndTipStartPage';

const FlipAndTipStartPage: FC = () => {
  const { route, navigate, crumbs } = useRoute();
  const { t } = useTranslation();
  const toast = useToast();

  const checkId = route.params.active.checkId ? parseInt(route.params.active.checkId, 10) : undefined;
  const paymentId = route.params.active.paymentId;

  const paymentFlow = usePaymentFlow();
  const checkGetOne = useCheck.query.getOne(checkId);
  const paymentAdjust = usePayment.mutate.adjust();
  
  const check = checkGetOne.data;
  const payments = check?.payments ?? [];
  const payment = payments.find(({ id }) => id === paymentId);

  // "future" - plans for switching between calculating tips w/subtotal(no tax) or calculating w/total(tax included)
  let basis = undefined as undefined | number;
  if (typeof paymentFlow.basis === 'number') {
    // "sometimes" - use basis for tip suggestions - i.e. - split or partial flows
    basis = paymentFlow.basis;
  } else if (payments.length === 1) {
    // "most times" - the basis should be undefined so the backend can calculate tip suggestions based on the check
    // this is the most common payment flow where we only have one payment
    basis = undefined;
  } else if (payments.length > 1) {
    // "sometimes" - we do not know how much was paid and if that included tax so use the payment amount as a basis
    basis = payment?.amountAppliedToCheck;
  }

  // "useCheck.query.getAllTipSuggestions" will block fetching when checkId is not defined
  // - i.e. - wait for check to load before getting tip suggestions
  const tipSuggestionsGetAll = useCheck.query.getAllTipSuggestions({
    checkId: check?.id,
    basis,
  });

  const tipSuggestions = tipSuggestionsGetAll.data?.suggestions;

  return (
    <Page
      mode="lite"
      className={styles.root}
      main={(
        <PageMain
          main={(
            <div className={styles.main}>
              {
                // block ui when data is not ready
                check && tipSuggestions && Boolean(tipSuggestions.length)
                  ? (
                    <>
                      <FlipAndTipForm
                        payment={payment}
                        tipSuggestions={tipSuggestions}
                        onComplete={({ tip, digitalSignature, total }) => {
                          if (typeof checkId !== 'number') return log.warn('form', 'onComplete', '!checkId');
                          if (typeof paymentId !== 'string') return log.warn('form', 'onComplete', '!paymentId');
                          paymentAdjust.mutate({
                            payment: {
                              checkId,
                              paymentId,
                              digitalSignature,
                              tip,
                              total,
                            },
                          }, {
                            onSuccess: async (resp) => {
                              if (resp.payment.paymentStatus === 'Declined') {
                                toast.showAndAutoHide.warning({
                                  id: `${sectionId}.adjust-fail`,
                                  title: t('payment.labels.declined'),
                                  message: t('payment.labels.paymentAdjustmentDeclined'),
                                });
                                return;
                              }
                              navigate(crumbs.pos.customer.flipAndTip.end.interpolate({ checkId, paymentId }));
                            },
                            onError: (err) => toast.show.serviceError(err, { id: `${sectionId}.paymentAdjustTip` }),
                          });
                        }}
                      />
                      <FlipAndTipCheck check={check} />
                    </>
                  )
                  : (
                    <AppLoader />
                  )
              }
            </div>
          )}
        />
      )}
    />
  );
};

// default export as this file is lazy loaded
export default FlipAndTipStartPage;
